import { Injectable } from '@angular/core';
import { Facility, FacilityConnection } from '../../model/locations/facility';
import { DirtyStatus } from '../../model/common/dirty-status';
import {
  onlyUnique,
  isNullOrEmptyArray,
} from '../../core/functions/common-functions';
import { FacilitySearchResult } from '../../model/locations/facility-search-results';
import { FacilityNotepadEntry } from '../../model/locations/facility-notepad-entry';
import {
  SortDirection,
  getCorrectedDate,
  sortByProperty,
} from '../../utils/utils';
import { FacilityNotepadCategory } from '../../model/locations/facility-notepad-category';
import {
  FacilitySignIn,
  FacilitySignInInput,
  FacilitySignInSearch,
  FacilitySignInSortOrQuery,
  PublicFacility,
} from '../../model/locations/facility-sign-in';
import { AOR } from 'src/app/model/locations/aor';

@Injectable({
  providedIn: 'root',
})
export class FacilityBuilderService {
  buildFacilitySearch(item: any): FacilitySearchResult {
    return {
      id: item.id,
      facilityName: item.facilityName,
      facilityUid: item.facilityUid,
      scadaId: item.scadaId,
      state: item.state,
      county: item.county,
      township: item.township,
      facilityType: item.facilityType,
      company: item.company,
      aors: item.aors,
      hidden: item.hidden,
    };
  }

  buildFacility(item: any, locations: any[] | null = null): Facility {
    if (!isNullOrEmptyArray(locations)) {
      const locIds = item.Locations ? item.Locations.map((l: any) => l.id) : [];
      const locs = locations
        ? locations.filter((l) => locIds.includes(l.id))
        : [];
      item.Locations = locs;
    }

    const states = item.Locations
      ? item.Locations.map((l: any) => l.state)
          .filter(onlyUnique)
          .join(', ')
      : '';
    const counties = item.Locations
      ? item.Locations.map((l: any) => l.county)
          .filter(onlyUnique)
          .join(', ')
      : '';
    const townships = item.Locations
      ? item.Locations.map((l: any) => l.township)
          .filter(onlyUnique)
          .join(', ')
      : '';

    const fac = {
      id: item.id,
      ContactListId: item.ContactListId,
      managementContactListId: item.managementContactListId,
      facilityName: item.facilityName,
      facilityUid: item.facilityUid,
      facilityType: item.facilityType
        ? item.facilityType
        : item.FacilityType?.name,
      FacilityType: item.FacilityType,
      address: item.address,
      company: item.Company ? item.Company.name : '',
      companyId: item.Company ? item.Company.id : '',
      notes: item.notes,
      gps: item.gps,
      phone: item.phone,
      altPhone: item.altPhone,
      scadaId: item.scadaId,
      state: states,
      county: counties,
      township: townships,
      locations:
        item.Locations && item.Locations.length > 0
          ? item.Locations.map((l: any) => ({
              id: l.id,
              state: l.state,
              county: l.county,
              township: l.township,
            }))
          : [],
      businessUnit: item.businessUnit,
      version: item.version,
      updater: item.updater,
      deletedAt: item.deletedAt,
      owner: item.owner,
      ownerId: item.owner ? item.owner.id : '',
      area: item.area,
      areaId: item.area ? item.area.id : '',
      flocNumber: item.flocNumber ? item.flocNumber : '',
      timezone: item.timezone,
      NotepadEntries:
        item.NotepadEntries?.map((entry: any) =>
          this.buildFacilityNotepadEntry(entry)
        ).sort((a: FacilityNotepadEntry, b: FacilityNotepadEntry) =>
          sortByProperty(a, b, 'displayDate', SortDirection.DESCENDING)
        ) ?? [],
      hidden: item.hidden,
      autoSignout: item.autoSignout,
      AdditionalResourceLists: item?.AdditionalResourceLists,
      AORs: item.AORs,
      aorsString: item.AORs?.map((aor: AOR) => aor.name).join(', ') ?? '',
    };

    return fac;
  }

  buildFacilityNotepadCategory(category: any): FacilityNotepadCategory {
    return {
      id: category.id,
      businessUnitId: category.id,
      BusinessUnit: category.BusinessUnit
        ? {
            id: category.BusinessUnit.id,
            name: category.BusinessUnit.name,
          }
        : null,
      name: category.name,
      enabled: category.enabled,
      deletedAt: getCorrectedDate(category.deletedAt)!,
    };
  }

  buildFacilityNotepadEntry(entry: any): FacilityNotepadEntry {
    const correctedCreated = getCorrectedDate(entry.createdAt)!;
    const correctedUpdated = getCorrectedDate(entry.updatedAt)!;
    return {
      id: entry.id,
      facilityId: entry.facilityId,
      categoryId: entry.categoryId,
      Category: this.buildFacilityNotepadCategory(entry.Category),
      note: entry.note,
      archived: entry.archived,
      createdBy: entry.createdBy,
      createdAt: correctedCreated,
      updater: entry.updater,
      updatedAt: correctedUpdated,
      deletedAt: getCorrectedDate(entry.deletedAt)!,
      version: entry.version,
      user: entry.updater ? entry.updater : entry.createdBy,
      time: correctedUpdated ? correctedUpdated : correctedCreated,
    };
  }

  buildFacilityQuery(filters: any) {
    const query = {
      state: null,
      county: null,
      township: null,
      aors: null,
      facilityName: null,
      company: null,
      facilityUid: null,
      scadaId: null,
      facilityType: null,
      businessUnits: null,
      hidden: null,
    };

    if (filters.state) {
      query.state = filters.state.value;
    }
    if (filters.county) {
      query.county = filters.county.value;
    }
    if (filters.township) {
      query.township = filters.township.value;
    }
    if (filters.aors) {
      query.aors = filters.aors.value;
    }
    if (filters.facilityName) {
      query.facilityName = filters.facilityName.value;
    }
    if (filters.company) {
      query.company = filters.company.value;
    }
    if (filters.facilityUid) {
      query.facilityUid = filters.facilityUid.value;
    }
    if (filters.scadaId) {
      query.scadaId = filters.scadaId.value;
    }
    if (filters.facilityType) {
      query.facilityType = filters.facilityType.value;
    }
    if (filters.businessUnits) {
      query.businessUnits = filters.businessUnits;
    }

    if (filters.hidden) {
      query.hidden = filters.hidden;
    }

    return query;
  }

  buildCreateStatement(facility: Facility, locations: any[]) {
    const retVal = {
      id: facility.id,
    };

    return retVal;
  }

  buildUpdateStatement(facility: Facility, facilityInput: any): any {
    const retVal: any = {
      id: facility.id,
      Locations: facilityInput.locations ? facilityInput.locations : [],
      businessUnit: facilityInput.businessUnit
        ? facilityInput.businessUnit
        : null,
      expectedVersion: facility.version,
    };

    retVal['address'] = facilityInput.hasOwnProperty('address')
      ? facilityInput.address
      : facility.address;
    retVal['gps'] = facilityInput.hasOwnProperty('gps')
      ? facilityInput.gps
      : facility.gps;
    retVal['timezone'] = facilityInput.hasOwnProperty('timezone')
      ? facilityInput.timezone?.value
      : facility.timezone;
    retVal['notes'] = facilityInput.hasOwnProperty('notes')
      ? facilityInput.notes
      : facility.notes;
    retVal['scadaId'] = facilityInput.hasOwnProperty('scadaId')
      ? facilityInput.scadaId
      : facility.scadaId;
    retVal['flocNumber'] = facilityInput.hasOwnProperty('flocNumber')
      ? facilityInput.flocNumber
      : facility.flocNumber;
    retVal['phone'] = facilityInput.hasOwnProperty('phone')
      ? facilityInput.phone
      : facility.phone;
    retVal['altPhone'] = facilityInput.hasOwnProperty('altPhone')
      ? facilityInput.altPhone
      : facility.altPhone;
    retVal['hidden'] = facilityInput.hasOwnProperty('hidden')
      ? facilityInput.hidden
      : facility.hidden;
    retVal['autoSignout'] = facilityInput.hasOwnProperty('autoSignout')
      ? facilityInput.autoSignout
      : facility.autoSignout;

    retVal['AdditionalResourceLists'] = facilityInput.hasOwnProperty(
      'AdditionalResourceLists'
    )
      ? facilityInput.AdditionalResourceLists
      : facility.AdditionalResourceLists;

    if (facilityInput.hasOwnProperty('facilityName')) {
      retVal['facilityName'] = facilityInput.facilityName;
    }
    if (facilityInput.hasOwnProperty('facilityUid')) {
      retVal['facilityUid'] = facilityInput.facilityUid;
    }
    if (facilityInput.hasOwnProperty('facilityType')) {
      retVal['facilityType'] = facilityInput.facilityType;
    }
    if (facilityInput.hasOwnProperty('aors')) {
      retVal['aors'] = facilityInput.aors;
    }
    if (facilityInput.hasOwnProperty('company')) {
      retVal['company'] = facilityInput.company;
    }
    if (facilityInput.hasOwnProperty('ContactListId')) {
      retVal['ContactListId'] = facilityInput.ContactListId;
    }
    if (facilityInput.hasOwnProperty('managementContactListId')) {
      retVal['managementContactListId'] = facilityInput.managementContactListId;
    }
    if (facilityInput.hasOwnProperty('owner')) {
      retVal['ownerId'] = facilityInput.owner?.value ?? facilityInput.owner;
    }
    if (facilityInput.hasOwnProperty('area')) {
      retVal['areaId'] = facilityInput.area?.value ?? facilityInput.area;
    }
    if (facilityInput.hasOwnProperty('flocNumber')) {
      retVal['flocNumber'] = facilityInput.flocNumber;
    }
    if (facilityInput.hasOwnProperty('timezone')) {
      retVal['timezone'] = facilityInput.timezone?.value;
    }

    return retVal;
  }

  buildNewFacilityLocation(facility: Facility, rawLocation: any): any {
    return {
      update: {
        LocationId: rawLocation.locationId,
        FacilityId: facility.id,
        dirtyStatus: DirtyStatus.NEW,
      },
      value: {
        id: rawLocation.locationId,
        state: rawLocation.state,
        county: rawLocation.county.county,
        township: rawLocation.township.township,
      },
    };
  }

  buildPublicFacility(facility: any): PublicFacility {
    const retVal = {
      id: facility.id,
      facilityName: facility.facilityName,
      facilityUid: facility.facilityUid,
    };
    return retVal;
  }

  buildFacilitySignIn(facilitySignIn: any): FacilitySignIn {
    const retVal = {
      id: facilitySignIn.id,
      name: facilitySignIn.name,
      contactNumber: facilitySignIn.contactNumber,
      location: facilitySignIn.facilityId,
      company: facilitySignIn.company,
      gaveConsent: facilitySignIn.gaveConsent,
      takenOrientation: facilitySignIn.takenOrientation,
      signInTime: getCorrectedDate(facilitySignIn?.signInTime)!,
      signOutTime: getCorrectedDate(facilitySignIn?.signOutTime)!,
    };

    return retVal;
  }

  buildFacilitySignInInput(facilitySignIn: any): FacilitySignInInput {
    const retVal = {
      name: facilitySignIn.name,
      contactNumber: facilitySignIn.contactNumber,
      facilityId: facilitySignIn.location.id,
      company: facilitySignIn.company,
      gaveConsent: facilitySignIn.gaveConsent,
      takenOrientation: facilitySignIn.takenOrientation,
    };

    return retVal;
  }

  buildFacilitySignInSearch(facilitySignIn: any): FacilitySignInSearch {
    const retVal = {
      id: facilitySignIn.id,
      name: facilitySignIn.name,
      contactNumber: facilitySignIn.contactNumber,
      location: facilitySignIn.facilityId,
      company: facilitySignIn.company,
      gaveConsent: facilitySignIn.gaveConsent,
      takenOrientation: facilitySignIn.takenOrientation,
      signInTime: facilitySignIn.signInTime,
      signOutTime: facilitySignIn?.signOutTime,
      Facility: facilitySignIn?.Facility,
      facilityName: facilitySignIn?.Facility.facilityName,
      Area: facilitySignIn?.Area,
      areaName: facilitySignIn?.Area?.name,
    };

    return retVal;
  }

  buildFacilitySignInQuery(filters: any) {
    const query: FacilitySignInSortOrQuery = {
      name: null,
      company: null,
      facilityName: null,
      areaName: null,
      signInTime: null,
      signOutTime: null,
      signedOut: false,
      contactNumber: null,
    };

    if (filters.name) {
      query.name = filters.name.value;
    }

    if (filters.company) {
      query.company = filters.company.value;
    }

    if (filters.facilityName) {
      query.facilityName = filters.facilityName.value;
    }

    if (filters.areaName) {
      query.areaName = filters.areaName.value;
    }

    if (filters.signInTime) {
      query.signInTime = filters.signInTime.value;
    }

    if (filters.signOutTime) {
      query.signOutTime = filters.signOutTime.value;
    }

    if (filters.signedOut) {
      query.signedOut = filters.signedOut;
    }

    if (filters.contactNumber) {
      query.contactNumber = filters.contactNumber.value;
    }

    return query;
  }

  buildFacilityConnection(item: any): FacilityConnection {
    return {
      items: item.items.map((i: any) => this.buildFacilitySearch(i)),
      totalRecords: item.totalRecords,
    };
  }
}
